  const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
import { useState, useEffect } from 'react';
import type { FC, ChangeEvent, FormEvent } from 'react';

interface SocialLinksState {
  x: string;
  facebook: string;
  instagram: string;
  linkedin: string;
}

const SocialSection: FC = () => {
  const [links, setLinks] = useState<SocialLinksState>({
    x: '',
    facebook: '',
    instagram: '',
    linkedin: '',
  });
  const [success, setSuccess] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);

  // Fetch existing social links on component mount
  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/social-links`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success && data.socialLinks) {
          setLinks({
            x: data.socialLinks.x || '',
            facebook: data.socialLinks.facebook || '',
            instagram: data.socialLinks.instagram || '',
            linkedin: data.socialLinks.linkedin || '',
          });
        } else {
          console.warn('Unexpected response format:', data);
        }
      } catch (error) {
        console.error('Error fetching social links:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        setError(`Failed to load existing social links: ${errorMessage}`);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchSocialLinks();
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLinks(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSuccess('');
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/social-links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(links),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setSuccess('Social links saved successfully!');
      } else {
        setError(data.message || 'Failed to save social links');
      }
    } catch (error) {
      console.error('Error saving social links:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(`Failed to save social links: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const socialFields: { key: keyof SocialLinksState; label: string; placeholder: string }[] = [
    { key: 'x', label: 'X', placeholder: 'https://x.com/username' },
    { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/username' },
    { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/username' },
    { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/username' },
  ];

  if (initialLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-0">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 sm:mb-8">Social Media Links</h1>
        <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl shadow-sm">
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-3 text-gray-600">Loading social links...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-0">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 sm:mb-8">Social Media Links</h1>

      <form onSubmit={handleSubmit} className="bg-white p-4 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl shadow-sm space-y-4 sm:space-y-6">
        {success && <div className="p-3 text-green-800 bg-green-100 rounded-lg">{success}</div>}
        {error && <div className="p-3 text-red-800 bg-red-100 rounded-lg">{error}</div>}
        
        {socialFields.map(field => (
          <div key={field.key}>
            <label htmlFor={field.key} className="block text-sm font-medium text-gray-700 mb-2">
              {field.label}
            </label>
            <input
              type="url"
              id={field.key}
              name={field.key}
              value={links[field.key]}
              onChange={handleInputChange}
              placeholder={field.placeholder}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl  transition"
            />
          </div>
        ))}
        
        <div>
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto bg-primary text-white px-8 py-3 rounded-xl hover:bg-primary/90 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </span>
            ) : (
              'Save Social Links'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SocialSection;